import React, { useRef } from 'react';

import { Box } from '@rbilabs/component-library';
import { defineMessages, useIntl } from 'react-intl';
import { Route, Routes, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { DeliveryAddressModal } from 'components/address-modal';
import FittedViewContainer from 'components/fitted-view-container';
import { GeolocationDeniedModal } from 'components/geolocation-denied-modal';
import { GeolocationModal } from 'components/geolocation-modal';
import { NoStoresDeliveryModal } from 'components/modal-no-delivery-stores';
import PickupModeModal from 'components/pickup-mode-modal';
import ServiceModeCateringModal from 'components/service-mode-catering-modal';
import ServiceModeModal from 'components/service-mode-modal';
import StoreInfoModalContainer from 'components/store-info-modal';
import { StoreLocatorEmptyStates as EmptyState } from 'components/store-locator-empty-states';
import { Tab, TabPanel, Tabs, TabsStyle } from 'components/tabs';
import { useStoreLocatorContentQuery } from 'generated/sanity-graphql';
import { useFavoriteStores } from 'hooks/favorite-stores';
import useMediaQuery from 'hooks/use-media-query';
import { StoreStatus } from 'remote/api/restaurants';
import { useCustomizableContent } from 'state/customizable-content';
import { useGeolocation } from 'state/geolocation';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { CustomEventNames, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { GeolocationPermissionStates } from 'utils/geolocation';
import DEFAULT_MAP_ZOOM from 'utils/restaurant/default-map-zoom';
import { relativeRoutes, routes } from 'utils/routing';

import { BackArrow } from './back-arrow';
import brandRoutes from './brand-routes';
import { DeliveryTab } from './delivery-tab';
import { GeoLocationAsk } from './geolocation-ask';
import { StoreLocatorProvider, StoreLocatorTabs, useStoreLocatorContext } from './hooks';
import ListView from './list-view';
import { MapContainer } from './map-container';
import StoreLocatorMeta from './meta';
import ResetLocationButton from './reset-location-button';
import SearchBar from './search-bar';
import { StoreCards } from './store-cards';
import { StoreCards as StoreCardsList } from './store-cards-list';
import { SearchContainer, ViewContainer } from './styled/view-container';
import theme from './theme';
// TODO: delete this component - no longer used
// import { TabDetailContent } from './tab-detail-content';
import ToggleViewButton from './toggle-view-button';

const TabContainer = styled.div`
  display: flex;
  position: sticky;
  top: 0;
  z-index: ${Styles.zIndex.normal};
  background-color: ${theme.tabBackground};
  padding-bottom: 1rem;
  ${Styles.desktop`
    top: 0px;
    padding: 0 1rem 0.5rem;
    margin: 0;
  `}
`;

const TabWrapper = styled.div`
  background-color: ${theme.tabBackground};
  width: 100%;
  padding: 0 1rem;
  ${Styles.desktop`
    width: 350px;
    padding: 0;
  `}
  ${TabsStyle} {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
`;

// used to smooth over transition into new, flagged slim-store-cards components
// cleanup when ENABLE_MAP_VIEW_MOBILE_VERTICAL_SCROLL_AND_SLIM_STORE_CARDS is removed
const Padding = styled.div`
  z-index: ${Styles.zIndex.top};
  padding: 1.5rem 1rem 0;
  position: relative;
`;

const ariaTabId = (tab: StoreLocatorTabs) => `store-locator-tab-${tab}`;
const ariaTabPanel = (tab: StoreLocatorTabs) => `store-locator-tabpanel-${tab}`;
function a11yPropsTabs(tab: StoreLocatorTabs) {
  return { id: ariaTabId(tab), ariaControls: ariaTabPanel(tab) };
}
function a11yPropsPanels(tab: StoreLocatorTabs) {
  return { id: ariaTabPanel(tab), ariaLabeledBy: ariaTabId(tab) };
}

export function StoreLocator() {
  const {
    ErrorDialog,
    Dialog,
    activeStoreId,
    hasPanned,
    map,
    onClickSearch,
    permission,
    redirectUrl,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    currentTabHasError,
    nearbyStoreCardRefs,
    nearbyStores,
    favStores,
    recentStores,
    zoom,
    resetLocation,
    displayMapViewTabs,
    enableStoreLocatorDeliveryTab,
    enableStoreLocatorRecentTab,
    showStoreLocatorTabs,
    showGeoAskInNearbyTabWhenEmpty,
    listViewEnabled,
    loadingUserCoordinates,
    showListView,
    currentTab,
    setIsStoreInfoModalOpen,
    handleTabChange,
    setActiveStoreId,
  } = useStoreLocatorContext();

  const { updateManualCoordinates, manualAddress, coordinatesAvailable } = useGeolocation();

  const { appDataId } = useCustomizableContent();
  const { data } = useStoreLocatorContentQuery({ variables: { appDataId } });
  const { storeLocatorContent } = data?.AppData || {};
  const { searchThisAreaButtonLabel } = storeLocatorContent || {};

  const { logNavigationClick } = useMParticleContext();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const [dismissAddressModalUrl, setDismissAddressModalUrl] = React.useState(routes.base);
  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = React.useState(false);

  const useSlimCards = useFlag(
    LaunchDarklyFlag.ENABLE_MAP_VIEW_MOBILE_VERTICAL_SCROLL_AND_SLIM_STORE_CARDS
  );
  const StoreCardsComponent = useSlimCards ? StoreCardsList : StoreCards;

  const enableCateringDelivery = useFlag(LaunchDarklyFlag.ENABLE_CATERING_DELIVERY);

  const { serviceMode } = useOrderContext();

  const { favStoresEnabled } = useFavoriteStores();

  const isDesktop = useMediaQuery('desktop');
  const isMobileFullScreen = useMediaQuery('mobileFullscreen');

  const handleTabChangeEvent = React.useCallback(
    (event: React.ChangeEvent<{}>, newValue: StoreLocatorTabs) => {
      event.preventDefault();
      logNavigationClick(CustomEventNames.BUTTON_CLICK_STORE_LOCATOR_TAB, {
        Tab: StoreLocatorTabs[newValue],
      });
      if (newValue === StoreLocatorTabs.Delivery) {
        navigate(routes.address);
        setDismissAddressModalUrl(routes.storeLocator);
        return;
      }

      handleTabChange(newValue);
    },
    [handleTabChange, logNavigationClick, navigate]
  );

  const openGeolocationDeniedModal = () => setGeolocationDeniedModalOpen(true);
  const dismissGeolocationDeniedModal = () => setGeolocationDeniedModalOpen(false);

  const isListView = listViewEnabled && showListView;

  const deliveryTabEnabled =
    serviceMode === ServiceMode.CATERING_DELIVERY || serviceMode === ServiceMode.CATERING_PICKUP
      ? enableCateringDelivery && enableStoreLocatorDeliveryTab
      : enableStoreLocatorDeliveryTab;

  const isSearchingNearby =
    (searchingNearby && !nearbyStores?.length) ||
    permission === GeolocationPermissionStates.CHECKING;

  const isSearchingRecent =
    (searchingRecent && !recentStores?.length) ||
    permission === GeolocationPermissionStates.CHECKING;

  const isSearchingFavorites =
    (searchingFavs && !favStores?.length) || permission === GeolocationPermissionStates.CHECKING;

  const searchAreaButtonText = searchThisAreaButtonLabel?.locale;

  const searchBarEl = (
    <SearchBar
      isListView={isListView}
      smallSize={listViewEnabled && !showListView}
      autoFocus={!manualAddress && permission !== GeolocationPermissionStates.GRANTED && isDesktop}
      value={manualAddress}
      onPlaceChanged={updateManualCoordinates}
    />
  );

  const noStoresElement = (
    <EmptyState
      tab={currentTab}
      locationPermissions={permission}
      coordinatesAvailable={coordinatesAvailable}
      currentTabError={currentTabHasError}
    />
  );

  const nearbyNoStoresElement = showGeoAskInNearbyTabWhenEmpty ? (
    <>
      {noStoresElement}
      <GeoLocationAsk isListView={isListView} />
    </>
  ) : (
    noStoresElement
  );

  const nearbyStoreCardsEl = (
    <StoreCardsComponent
      isListView={isListView}
      searching={isSearchingNearby}
      stores={nearbyStores}
      noStoresElement={nearbyNoStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const favStoreCardsEl = (
    <StoreCardsComponent
      isListView={isListView}
      searching={isSearchingFavorites}
      stores={favStores}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const recentStoreCardsEl = (
    <StoreCardsComponent
      isListView={isListView}
      searching={isSearchingRecent}
      stores={recentStores}
      noStoresElement={noStoresElement}
      nearbyStoreCardRefs={nearbyStoreCardRefs}
      setActiveStoreId={setActiveStoreId}
      activeStoreId={activeStoreId}
    />
  );

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabContainerRef = useRef<HTMLDivElement | null>(null);

  const tabsEl = showStoreLocatorTabs && (
    <TabWrapper>
      <Tabs tabWidth="flex" selectedIndex={currentTab} onChange={handleTabChangeEvent}>
        <Tab
          label={formatMessage(messages.nearby)}
          {...a11yPropsTabs(StoreLocatorTabs.Nearby)}
          value={StoreLocatorTabs.Nearby}
        />
        {enableStoreLocatorRecentTab && (
          <Tab
            label={formatMessage(messages.recent)}
            {...a11yPropsTabs(StoreLocatorTabs.Recent)}
            value={StoreLocatorTabs.Recent}
            data-testid="recentTab"
          />
        )}
        {favStoresEnabled && (
          <Tab
            label={formatMessage(messages.favorite)}
            {...a11yPropsTabs(StoreLocatorTabs.Favorites)}
            value={StoreLocatorTabs.Favorites}
          />
        )}
        {deliveryTabEnabled && (
          <DeliveryTab
            label={formatMessage(messages.delivery)}
            {...a11yPropsTabs(StoreLocatorTabs.Delivery)}
            value={StoreLocatorTabs.Delivery}
          />
        )}
      </Tabs>
    </TabWrapper>
  );

  const tabPanelsEl = (
    <>
      <TabPanel
        value={currentTab}
        index={StoreLocatorTabs.Nearby}
        {...a11yPropsPanels(StoreLocatorTabs.Nearby)}
      >
        {nearbyStoreCardsEl}
      </TabPanel>
      <TabPanel
        value={currentTab}
        index={StoreLocatorTabs.Favorites}
        {...a11yPropsPanels(StoreLocatorTabs.Favorites)}
      >
        {favStoreCardsEl}
      </TabPanel>
      <TabPanel
        value={currentTab}
        index={StoreLocatorTabs.Recent}
        {...a11yPropsPanels(StoreLocatorTabs.Recent)}
      >
        {recentStoreCardsEl}
      </TabPanel>
    </>
  );

  /*
   * NOTE: We want to only render the map once
   * So we "hide" / `display: none` the map container when on the list view
   */

  const mapViewEl = (
    <>
      {!isDesktop && !showListView && searchBarEl}
      <MapContainer map={map} hide={showListView}>
        {!showListView && searchAreaButtonText && (
          <>
            <Box
              position="absolute"
              center
              bottom="1rem"
              show={hasPanned && zoom >= DEFAULT_MAP_ZOOM}
              margin={isMobileFullScreen ? '0 0 env(safe-area-inset-bottom)' : '0'}
            >
              <ActionButton
                size={isDesktop ? ActionButtonSizes.LARGE : ActionButtonSizes.SMALL}
                onClick={onClickSearch}
              >
                {formatMessage({ id: 'searchThisArea' })}
              </ActionButton>
            </Box>

            <Box
              position="absolute"
              bottom={isDesktop ? '2rem' : '1rem'}
              right={isDesktop ? '2rem' : '1rem'}
            >
              <ResetLocationButton
                onClick={
                  permission === GeolocationPermissionStates.GRANTED
                    ? resetLocation
                    : openGeolocationDeniedModal
                }
                loading={loadingUserCoordinates}
              />
            </Box>
          </>
        )}
      </MapContainer>
      {!showListView && (
        <SearchContainer withSlimCards={useSlimCards} ref={scrollContainerRef}>
          {isDesktop && <Padding>{searchBarEl}</Padding>}
          {displayMapViewTabs && !!tabsEl && (
            <div>
              <TabContainer ref={tabContainerRef}>{tabsEl}</TabContainer>
              <div>{tabPanelsEl}</div>
            </div>
          )}
          {!displayMapViewTabs && nearbyStoreCardsEl}
        </SearchContainer>
      )}
    </>
  );

  return (
    <>
      <StoreLocatorMeta />
      <GeolocationDeniedModal
        isOpen={geolocationDeniedModalOpen}
        dismissModal={dismissGeolocationDeniedModal}
      />
      <FittedViewContainer>
        <BackArrow />
        <ViewContainer data-testid="store-locator-container">
          <ToggleViewButton />
          {mapViewEl}
          <ListView tabsEl={tabsEl} tabPanelsEl={tabPanelsEl} searchBarEl={searchBarEl} />
          <Routes>
            <Route path={relativeRoutes.serviceMode} element={<ServiceModeModal />} />
            <Route
              path={relativeRoutes.serviceModeCatering}
              element={<ServiceModeCateringModal />}
            />
            <Route
              path={relativeRoutes.serviceModeCateringPickup}
              element={<ServiceModeCateringModal />}
            />
            <Route
              path={relativeRoutes.serviceModeCateringDelivery}
              element={<ServiceModeCateringModal />}
            />
            <Route
              path={relativeRoutes.pickupMode}
              element={<PickupModeModal redirectUrl={redirectUrl} />}
            />
            <Route
              path={relativeRoutes.address}
              element={
                <DeliveryAddressModal
                  redirectUrl={redirectUrl}
                  dismissAddressModalUrl={dismissAddressModalUrl}
                />
              }
            />
            <Route
              path={relativeRoutes[StoreStatus.NO_DELIVERY]}
              element={<NoStoresDeliveryModal />}
            />
            {Object.entries(brandRoutes).map(([path, Component]) => (
              <Route
                key={`${path}-path`}
                path={path}
                element={<Component key={`${path}-component`} />}
              />
            ))}
            <Route path={relativeRoutes.locationService} element={<GeolocationModal />} />
            <Route
              path={`${relativeRoutes.store}/:id`}
              element={
                <StoreInfoModalContainer
                  onDismiss={() => {
                    navigate('.');
                    setIsStoreInfoModalOpen(false);
                  }}
                  onOpen={() => setIsStoreInfoModalOpen(true)}
                />
              }
            />
            <Route
              path={`${relativeRoutes.store}/:storeNumber/*`}
              element={
                <StoreInfoModalContainer
                  onDismiss={() => {
                    navigate('.');
                    setIsStoreInfoModalOpen(false);
                  }}
                  onOpen={() => setIsStoreInfoModalOpen(true)}
                />
              }
            />
          </Routes>
        </ViewContainer>
      </FittedViewContainer>
      <ErrorDialog />
      <Dialog />
    </>
  );
}

function StoreLocatorWithProvider() {
  return (
    <StoreLocatorProvider>
      <StoreLocator />
    </StoreLocatorProvider>
  );
}

export default StoreLocatorWithProvider;
const messages = defineMessages({
  delivery: {
    id: 'delivery',
    defaultMessage: 'Delivery',
    description: 'No description',
  },

  favorite: {
    id: 'favorite',
    defaultMessage: 'Favorites',
    description: 'No description',
  },

  recent: {
    id: 'recent',
    defaultMessage: 'Recent',
    description: 'No description',
  },

  nearby: {
    id: 'nearby',
    defaultMessage: 'Nearby',
    description: 'No description',
  },
});
